import React, { useState, useMemo, useEffect } from 'react';
import { useSafeClose } from '../../../../hooks/useSafeClose';
import { getResourceColor, getInitials } from '../../../../utils/resourceUtils';
import './ResourceSelectionModal.css';

/**
 * Helper to determine CSS class for role badge.
 * 
 * @param {string} role - The worker's role name.
 * @returns {string} The CSS class modifier.
 */
const getRoleBadgeClass = (role) => {
    const r = (role || '').toLowerCase();
    if (r === 'polier') return 'polier';
    if (r === 'monteur') return 'monteur';
    if (r === 'vorarbeiter') return 'vorarbeiter';
    return 'default';
};

/**
 * ResourceSelectionModal Component.
 * Standardized dialog for selecting workers, vehicles, and kolonnen.
 * 
 * @component
 */
const ResourceSelectionModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    allWorkers = [], 
    allVehicles = [], 
    selectedWorkers = [], 
    selectedVehicles = [],
    vehicleCategories = [],
    kolonnen = []
}) => {
    const [search, setSearch] = useState('');
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [workerIds, setWorkerIds] = useState([]);
    const [vehicleIds, setVehicleIds] = useState([]);

    // Sync state when modal opens
    useEffect(() => {
        if (isOpen) {
            setWorkerIds(selectedWorkers.map(w => w.worker_id));
            setVehicleIds(selectedVehicles.map(v => v.vehicle_id));
            setSearch('');
        }
    }, [isOpen, selectedWorkers, selectedVehicles]);

    /**
     * Resolves the vehicle category name/type.
     * 
     * @param {Object} v - Vehicle object.
     * @returns {string|null} Vehicle type label.
     */
    const getVehicleType = (v) => {
        if (v.vehicle_type) return v.vehicle_type;
        const cat = vehicleCategories.find(c => String(c.id) === String(v.category_id));
        return cat ? cat.name : null;
    };

    /**
     * Resolves the color of the vehicle based on its category.
     * 
     * @param {Object} v - Vehicle object.
     * @returns {string} Hex color code.
     */
    const getVehicleColor = (v) => {
        const cat = vehicleCategories.find(c => String(c.id) === String(v.category_id));
        return cat?.color || '#8e8e93';
    };

    const filteredWorkers = useMemo(() => {
        let list = [...allWorkers].filter(w => 
            (w.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (w.role || '').toLowerCase().includes(search.toLowerCase())
        );
        list.sort((a, b) => {
            const res = (a.name || '').localeCompare(b.name || '');
            return sortOrder === 'asc' ? res : -res;
        });
        return list;
    }, [allWorkers, search, sortOrder]);

    const filteredVehicles = useMemo(() => {
        let list = [...allVehicles].filter(v => 
            (v.license_plate || '').toLowerCase().includes(search.toLowerCase()) ||
            (v.internal_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (v.category_name || '').toLowerCase().includes(search.toLowerCase())
        );
        list.sort((a, b) => {
            const res = (a.license_plate || '').localeCompare(b.license_plate || '');
            return sortOrder === 'asc' ? res : -res;
        });
        return list;
    }, [allVehicles, search, sortOrder]);

    const filteredKolonnen = useMemo(() => {
        let list = [...(kolonnen || [])].filter(k => 
            (k.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (k.code || '').toLowerCase().includes(search.toLowerCase())
        );
        list.sort((a, b) => {
            const res = (a.name || '').localeCompare(b.name || '');
            return sortOrder === 'asc' ? res : -res;
        });
        return list;
    }, [kolonnen, search, sortOrder]);

    const toggleWorker = (id) => {
        setWorkerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleVehicle = (id) => {
        setVehicleIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleKolonne = (kolonne) => {
        const memberIds = (kolonne.members || [])
            .filter(m => m.type === 'worker')
            .map(m => m.resource_id);
        const vehicleIdsInK = (kolonne.members || [])
            .filter(m => m.type === 'vehicle')
            .map(m => m.resource_id);
            
        const allWorkersSelected = memberIds.every(id => workerIds.includes(id));
        const allVehiclesSelected = vehicleIdsInK.every(id => vehicleIds.includes(id));
        const allSelected = allWorkersSelected && allVehiclesSelected;
        
        if (allSelected) {
            setWorkerIds(prev => prev.filter(id => !memberIds.includes(id)));
            setVehicleIds(prev => prev.filter(id => !vehicleIdsInK.includes(id)));
        } else {
            setWorkerIds(prev => {
                const newIds = [...prev];
                memberIds.forEach(id => {
                    if (!newIds.includes(id)) newIds.push(id);
                });
                return newIds;
            });
            setVehicleIds(prev => {
                const newIds = [...prev];
                vehicleIdsInK.forEach(id => {
                    if (!newIds.includes(id)) newIds.push(id);
                });
                return newIds;
            });
        }
    };

    const isKolonneSelected = (k) => {
        if (!k.members || k.members.length === 0) return false;
        return k.members.every(m => 
            (m.type === 'worker' && workerIds.includes(m.resource_id)) ||
            (m.type === 'vehicle' && vehicleIds.includes(m.resource_id))
        );
    };

    const isKolonneIndeterminate = (k) => {
        if (!k.members || k.members.length === 0) return false;
        const selectedCount = k.members.filter(m => 
            (m.type === 'worker' && workerIds.includes(m.resource_id)) ||
            (m.type === 'vehicle' && vehicleIds.includes(m.resource_id))
        ).length;
        return selectedCount > 0 && selectedCount < k.members.length;
    };

    const handleConfirm = () => {
        onConfirm(workerIds, vehicleIds);
        onClose();
    };

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    return (
        <div className="res-modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="res-modal-content" onClick={e => e.stopPropagation()}>
                <div className="res-modal-header">
                    <div>
                        <h3>👥 Ressourcen auswählen</h3>
                        <p className="res-modal-subtitle">Wähle Mitarbeiter und Fahrzeuge aus der Liste</p>
                    </div>
                    <button className="res-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="res-modal-controls">
                    <div className="res-search-input">
                        <span className="icon">🔍</span>
                        <input 
                            type="text" 
                            placeholder="Nach Name, Rolle, Kennzeichen oder Fahrzeugtyp suchen..." 
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <button 
                        className={`btn-sort-toggle ${sortOrder === 'asc' ? 'active' : ''}`}
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                        {sortOrder === 'asc' ? 'A-Z ↓' : 'Z-A ↑'}
                    </button>
                </div>

                <div className="res-modal-body">
                    {/* MITARBEITER COLUMN */}
                    <div className="res-column">
                        <div className="column-header">
                            <h4>Mitarbeiter ({filteredWorkers.length})</h4>
                            <span className="selection-count">{workerIds.length} ausgewählt</span>
                        </div>
                        <div className="res-items-container">
                            {filteredWorkers.length === 0 ? (
                                <div className="no-results">Keine Mitarbeiter gefunden</div>
                            ) : (
                                filteredWorkers.map(w => (
                                    <label key={w.id} className={`res-selection-item ${workerIds.includes(w.id) ? 'selected' : ''}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={workerIds.includes(w.id)}
                                            onChange={() => toggleWorker(w.id)}
                                        />
                                        <div className="res-avatar" style={{ background: getResourceColor(w) }}>
                                            {getInitials(w.name)}
                                        </div>
                                        <div className="item-info">
                                            <span className="main-text">{w.name}</span>
                                            <span className={`res-role-badge ${getRoleBadgeClass(w.role)}`}>
                                                {w.role || 'Monteur'}
                                            </span>
                                        </div>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="vertical-divider"></div>

                    {/* FAHRZEUGE COLUMN */}
                    <div className="res-column">
                        <div className="column-header">
                            <h4>Fahrzeuge ({filteredVehicles.length})</h4>
                            <span className="selection-count">{vehicleIds.length} ausgewählt</span>
                        </div>
                        <div className="res-items-container">
                            {filteredVehicles.length === 0 ? (
                                <div className="no-results">Keine Fahrzeuge gefunden</div>
                            ) : (
                                filteredVehicles.map(v => (
                                    <label key={v.id} className={`res-selection-item ${vehicleIds.includes(v.id) ? 'selected' : ''}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={vehicleIds.includes(v.id)}
                                            onChange={() => toggleVehicle(v.id)}
                                        />
                                        <div 
                                            className="res-avatar" 
                                            style={{ 
                                                background: getVehicleColor(v),
                                                color: (v.category_id && vehicleCategories.find(c => String(c.id) === String(v.category_id))?.color && (
                                                    // Check if category color is light
                                                    (() => {
                                                        const hex = vehicleCategories.find(c => String(c.id) === String(v.category_id)).color;
                                                        const cleanHex = hex.replace('#', '');
                                                        if (cleanHex.length === 6) {
                                                            const r = parseInt(cleanHex.substring(0, 2), 16);
                                                            const g = parseInt(cleanHex.substring(2, 4), 16);
                                                            const b = parseInt(cleanHex.substring(4, 6), 16);
                                                            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                                                            return brightness > 180 ? '#1d1d1f' : '#ffffff';
                                                        }
                                                        return '#ffffff';
                                                    })()
                                                )) || '#ffffff'
                                            }}
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="res-vehicle-svg">
                                                <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
                                                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                                                <circle cx="5.5" cy="18.5" r="2.5" />
                                                <circle cx="18.5" cy="18.5" r="2.5" />
                                            </svg>
                                        </div>
                                        <div className="item-info vehicle-info">
                                            <div className="vehicle-main">
                                                <span className="main-text">{v.license_plate || 'Unbekannt'}</span>
                                                {v.internal_name && <span className="sub-text vehicle-nr">Nr. {v.internal_name}</span>}
                                            </div>
                                            {getVehicleType(v) && (
                                                <span className="res-vehicle-type-badge">
                                                    {getVehicleType(v)}
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="vertical-divider"></div>

                    {/* KOLONNEN COLUMN */}
                    <div className="res-column">
                        <div className="column-header">
                            <h4>Kolonnen ({filteredKolonnen.length})</h4>
                            <span className="selection-count">{filteredKolonnen.filter(k => isKolonneSelected(k)).length} ausgewählt</span>
                        </div>
                        <div className="res-items-container">
                            {filteredKolonnen.length === 0 ? (
                                <div className="no-results">Keine Kolonnen gefunden</div>
                            ) : (
                                filteredKolonnen.map(k => (
                                    <label key={k.id} className={`res-selection-item ${isKolonneSelected(k) ? 'selected' : ''} ${isKolonneIndeterminate(k) ? 'indeterminate' : ''}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={isKolonneSelected(k)}
                                            ref={el => el && (el.indeterminate = isKolonneIndeterminate(k))}
                                            onChange={() => toggleKolonne(k)}
                                        />
                                        <div className="res-avatar kolonne" style={{ background: '#3b82f6' }}>
                                            {k.code || 'K'}
                                        </div>
                                        <div className="item-info">
                                            <span className="main-text">{k.name}</span>
                                            <span className="sub-text kolonne-member-count">
                                                {k.members?.length || 0} Ress.
                                            </span>
                                        </div>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="res-modal-footer">
                    <button className="btn-panel-secondary" onClick={onClose}>Abbrechen</button>
                    <button className="btn-panel-primary" onClick={handleConfirm}>
                        {workerIds.length + vehicleIds.length} Ressourcen bestätigen
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResourceSelectionModal;
